import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { BrandTable } from './components';
import type { Brand } from './components/brandsMeta';

type BrandsPageViewProps = {
    data: Brand[];
    onAddBrand: () => void;
    onDeleteBrand: (id: string) => void;
    onEditBrand: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onSearchChange: (value: string) => void;
    page: number;
    pageSize: number;
    search: string;
    totalCount: number;
    totalPages: number;
};

export const BrandsPageView: React.FC<BrandsPageViewProps> = ({
    data,
    onAddBrand,
    onDeleteBrand,
    onEditBrand,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    page,
    pageSize,
    search,
    totalCount,
    totalPages,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            searchPlaceholder: t(
                'brands.components.brandTable.searchPlaceholder',
            ),
            searchAria: t('brands.components.brandTable.searchAria'),
            addBrand: t('brands.components.brandTable.addBrand'),
            tableAria: t('brands.components.brandTable.tableAria'),
        }),
        [t],
    );

    return (
        <section
            aria-label={copy.tableAria}
            {...getNodeTestIdProp('brand-table-section')}
        >
            <div
                className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                {...getNodeTestIdProp('brand-table-toolbar')}
            >
                <label
                    className="flex w-full items-center gap-2 rounded-xl border border-border bg-surface-secondary px-3 py-2 sm:w-auto sm:shrink-0"
                    htmlFor="brand-table-search"
                    {...getNodeTestIdProp('brand-table-search-wrap')}
                >
                    <Icon
                        aria-hidden
                        className="size-4 text-muted"
                        icon="gravity-ui:magnifier"
                    />
                    <input
                        aria-label={copy.searchAria}
                        className="w-full bg-transparent text-sm text-foreground placeholder:text-muted focus:outline-none sm:w-56"
                        id="brand-table-search"
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder={copy.searchPlaceholder}
                        type="text"
                        value={search}
                        {...getNodeTestIdProp('brand-table-search')}
                    />
                </label>
                <Button
                    onPress={onAddBrand}
                    variant="primary"
                    {...getNodeTestIdProp('brand-table-add')}
                >
                    <Icon icon="gravity-ui:plus" />
                    {copy.addBrand}
                </Button>
            </div>
            <BrandTable
                data={data}
                onDeleteBrand={onDeleteBrand}
                onEditBrand={onEditBrand}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                totalPages={totalPages}
            />
        </section>
    );
};
